import React from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  ColumnFiltersState,
  getPaginationRowModel,
} from "@tanstack/react-table";
import Pagination from "../../grid/Pagination";
import { fromJS } from "immutable";
import ChartLoading from "./ChartLoading";

interface DataTableProps {
  columns: Array<{ key: string; title: string }>;
  rows: Array<any>;
  loading?: boolean;
  getValue?: (row: any, key: string) => React.ReactNode;
  pagination?: any;
  onChangePage?: (page: number, size: number) => void;
}

const DataTable: React.FC<DataTableProps> = ({
  columns,
  rows,
  loading = false,
  getValue,
  pagination,
  onChangePage,
}) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  const columnHelper = createColumnHelper<any>();

  const tableColumns = React.useMemo(
    () =>
      columns.map((col) =>
        columnHelper.accessor(col.key, {
          id: col.key,
          header: col.title,
          minSize: 200,
          cell: ({ getValue: getCellValue, row }) => {
            const value = getCellValue();
            if (getValue) {
              return getValue(row.original, col.key);
            }
            return value;
          },
        })
      ),
    [columns, columnHelper, getValue]
  );

  const table = useReactTable({
    data: rows,
    columns: tableColumns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (loading) {
    return <ChartLoading height={270} className="h-[270px]" label="Loading table data..." />;
  }

  return (
    <div className="overflow-x-auto">
      <div className="border border-gray-200 rounded-lg overflow-hidden h-full bg-white">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-gray-200">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    style={{
                      width: header.getSize(),
                    }}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    <div className="flex items-center space-x-1">
                      <span>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.getIsSorted() === "asc" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {header.column.getIsSorted() === "desc" && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="hover:bg-gray-50">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className="px-4 py-3 text-sm text-gray-900"
                    style={{
                      width: cell.column.getSize(),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination ? (
        <div className="flex h-[62px] items-center justify-between">
          <p className="text-sm text-gray-500">
            {Math.min(rows.length, pagination?.size || 0)} of {pagination?.totalElements || 0}{" "}
            results
          </p>
          <div className="w-[200px] flex justify-end">
            <Pagination
              onChange={(page, size) => onChangePage && onChangePage(page, size)}
              pagination={fromJS(pagination || {})}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DataTable;
